/**
 * useFSTLocation — GPS tracking hook for FST mobile users.
 *
 * When active, watches the browser's geolocation and periodically sends
 * updates to POST /fst/location.  Only runs when `enabled` is true
 * (caller should gate on isFST + isMobileRole).
 *
 * Privacy: tracking only runs while the React tree is mounted (i.e. the
 * user is logged in and the app is open). The watcher and timers are
 * cleaned up on unmount / logout.
 *
 * Returns { myLocation, locationError, permissionState }
 *   myLocation:      { latitude, longitude, accuracy, timestamp } | null
 *   locationError:   string | null  (user-friendly message)
 *   permissionState: "prompt" | "granted" | "denied" | null
 */

import { useState, useEffect, useRef, useCallback } from "react";
import api from "../api/client";

// How often to POST the location to the server (ms)
const SEND_INTERVAL = 15_000;

// Minimum movement (meters) to trigger an immediate server update
const MIN_DISTANCE_M = 30;

export default function useFSTLocation(enabled = false) {
  const [myLocation, setMyLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [permissionState, setPermissionState] = useState(null);

  const lastSent = useRef(null);       // last coords sent to server
  const latestLoc = useRef(null);      // latest position (for interval closure)
  const sendTimer = useRef(null);
  const watchId = useRef(null);

  // Check permission state (non-blocking)
  useEffect(() => {
    if (!enabled || !navigator.permissions) return;
    navigator.permissions
      .query({ name: "geolocation" })
      .then((result) => {
        setPermissionState(result.state);
        const handler = () => setPermissionState(result.state);
        result.addEventListener("change", handler);
        return () => result.removeEventListener("change", handler);
      })
      .catch(() => {}); // some browsers don't support permissions API
  }, [enabled]);

  // Send location to backend
  const sendToServer = useCallback(
    async (lat, lng, acc) => {
      try {
        await api.post("/fst/location", {
          latitude: lat,
          longitude: lng,
          accuracy: acc ?? null,
        });
        lastSent.current = { lat, lng, time: Date.now() };
      } catch {
        // Silently ignore — network hiccups are common on mobile
      }
    },
    []
  );

  // Haversine distance (meters) between two points
  const distanceM = (a, b) => {
    const R = 6371000;
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const sinLat = Math.sin(dLat / 2);
    const sinLng = Math.sin(dLng / 2);
    const h =
      sinLat * sinLat +
      Math.cos((a.lat * Math.PI) / 180) *
        Math.cos((b.lat * Math.PI) / 180) *
        sinLng * sinLng;
    return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  };

  useEffect(() => {
    if (!enabled) return;
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by this browser.");
      return;
    }

    setLocationError(null);

    // Start watching position — this triggers the permission prompt
    // if permission has not been granted yet.
    watchId.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        const loc = { latitude, longitude, accuracy, timestamp: Date.now() };
        setMyLocation(loc);
        latestLoc.current = loc;
        setLocationError(null);

        // Send immediately if moved significantly or first update
        const prev = lastSent.current;
        if (
          !prev ||
          distanceM({ lat: prev.lat, lng: prev.lng }, { lat: latitude, lng: longitude }) >= MIN_DISTANCE_M
        ) {
          sendToServer(latitude, longitude, accuracy);
        }
      },
      (err) => {
        if (err.code === 1) {
          // PERMISSION_DENIED
          setPermissionState("denied");
          setLocationError(
            "Location access was denied. To enable field tracking, please allow location access in your browser settings."
          );
        } else if (err.code === 2) {
          setLocationError("Unable to determine your location. Please check your GPS settings.");
        } else {
          setLocationError("Location request timed out. Will retry automatically.");
        }
      },
      {
        enableHighAccuracy: true,
        maximumAge: 10_000,
        timeout: 30_000,
      }
    );

    // Periodic send timer — ensures server gets updates even if user is stationary
    sendTimer.current = setInterval(() => {
      const loc = latestLoc.current;
      if (loc) {
        sendToServer(loc.latitude, loc.longitude, loc.accuracy);
      }
    }, SEND_INTERVAL);

    // Cleanup on unmount / disable (logout, navigate away)
    return () => {
      if (watchId.current !== null) {
        navigator.geolocation.clearWatch(watchId.current);
        watchId.current = null;
      }
      if (sendTimer.current) {
        clearInterval(sendTimer.current);
        sendTimer.current = null;
      }
    };
  }, [enabled, sendToServer]);

  return { myLocation, locationError, permissionState };
}
